import type { Coord, PieceId, PieceKind, SpecialType } from '../core/types';
import type { Grid } from './grid';

export interface ActivationResult {
  clearCells: Coord[];
  convertToSpecial?: Array<{ at: Coord; special: SpecialType; kind: PieceKind; beamAxis?: 'h' | 'v' }>;
  chainActivations?: Array<{ at: Coord; piece: PieceId }>;
  scoreBonus: number;
  label: string;
}

/** Activate a single special at position. */
export function activateSpecial(
  grid: Grid,
  at: Coord,
  piece: PieceId,
  partner?: PieceId
): ActivationResult {
  if (!piece.special) {
    return { clearCells: [at], scoreBonus: 0, label: 'none' };
  }

  // Combo of two specials
  if (partner?.special) {
    return activateCombo(grid, at, piece, partner);
  }

  switch (piece.special) {
    case 'beam':
      return activateBeam(grid, at, piece.beamAxis ?? 'h');
    case 'nova':
      return activateNova(grid, at, 2);
    case 'burst':
      return activateBurst(grid, at);
    case 'prism':
      return activatePrism(grid, at, piece.prismKind ?? piece.kind);
    default:
      return { clearCells: [at], scoreBonus: 0, label: 'none' };
  }
}

function activateBeam(grid: Grid, at: Coord, axis: 'h' | 'v'): ActivationResult {
  const cells: Coord[] = [];
  if (axis === 'h') {
    for (let c = 0; c < grid.cols; c++) {
      if (grid.active(at.r, c)) cells.push({ r: at.r, c });
    }
  } else {
    for (let r = 0; r < grid.rows; r++) {
      if (grid.active(r, at.c)) cells.push({ r, c: at.c });
    }
  }
  return { clearCells: cells, scoreBonus: 80, label: `beam_${axis}` };
}

function activateNova(grid: Grid, at: Coord, radius: number): ActivationResult {
  const cells: Coord[] = [];
  for (let r = at.r - radius; r <= at.r + radius; r++) {
    for (let c = at.c - radius; c <= at.c + radius; c++) {
      if (grid.active(r, c)) cells.push({ r, c });
    }
  }
  return { clearCells: cells, scoreBonus: 120, label: `nova_r${radius}` };
}

function activateBurst(grid: Grid, at: Coord): ActivationResult {
  const cells: Coord[] = [];
  for (let r = at.r - 1; r <= at.r + 1; r++) {
    for (let c = at.c - 1; c <= at.c + 1; c++) {
      if (grid.active(r, c)) cells.push({ r, c });
    }
  }
  return { clearCells: cells, scoreBonus: 100, label: 'burst' };
}

function activatePrism(grid: Grid, at: Coord, kind: PieceKind): ActivationResult {
  const cells: Coord[] = [{ r: at.r, c: at.c }];
  grid.forEachActive((r, c, cell) => {
    if (cell.piece?.kind === kind) cells.push({ r, c });
  });
  return { clearCells: cells, scoreBonus: 200, label: `prism_${kind}` };
}

/**
 * Combo outcomes (greater than either alone):
 * beam+beam => double cross (row+col at both)
 * beam+burst => three parallel beams
 * beam+nova => giant cross with nova center
 * beam+prism => beams through every prism-color piece
 * burst+burst => large 5x5
 * burst+nova => mega nova r=3
 * nova+nova => full board pulse (almost)
 * nova+prism => convert all of kind to bursts then detonate
 * burst+prism => clear kind + local burst
 * prism+prism => clear two most common kinds
 */
function activateCombo(
  grid: Grid,
  at: Coord,
  a: PieceId,
  b: PieceId
): ActivationResult {
  const types = [a.special!, b.special!].sort() as [SpecialType, SpecialType];
  const key = `${types[0]}+${types[1]}`;

  switch (key) {
    case 'beam+beam': {
      const cells: Coord[] = [];
      for (let c = 0; c < grid.cols; c++) if (grid.active(at.r, c)) cells.push({ r: at.r, c });
      for (let r = 0; r < grid.rows; r++) if (grid.active(r, at.c)) cells.push({ r, c: at.c });
      // second cross offset
      const r2 = Math.min(grid.rows - 1, at.r + 1);
      const c2 = Math.min(grid.cols - 1, at.c + 1);
      for (let c = 0; c < grid.cols; c++) if (grid.active(r2, c)) cells.push({ r: r2, c });
      for (let r = 0; r < grid.rows; r++) if (grid.active(r, c2)) cells.push({ r, c: c2 });
      return { clearCells: uniq(cells), scoreBonus: 400, label: 'combo_double_cross' };
    }
    case 'beam+burst': {
      const cells: Coord[] = [];
      for (const row of [at.r - 1, at.r, at.r + 1]) {
        if (row < 0 || row >= grid.rows) continue;
        for (let c = 0; c < grid.cols; c++) if (grid.active(row, c)) cells.push({ r: row, c });
      }
      return { clearCells: uniq(cells), scoreBonus: 350, label: 'combo_triple_beam' };
    }
    case 'beam+nova': {
      const cells = [
        ...activateBeam(grid, at, 'h').clearCells,
        ...activateBeam(grid, at, 'v').clearCells,
        ...activateNova(grid, at, 2).clearCells,
      ];
      return { clearCells: uniq(cells), scoreBonus: 380, label: 'combo_cross_nova' };
    }
    case 'beam+prism': {
      const kind = a.special === 'prism' ? a.prismKind ?? a.kind : b.prismKind ?? b.kind;
      const cells: Coord[] = [];
      grid.forEachActive((r, c, cell) => {
        if (cell.piece?.kind === kind) {
          for (let cc = 0; cc < grid.cols; cc++) if (grid.active(r, cc)) cells.push({ r, c: cc });
          for (let rr = 0; rr < grid.rows; rr++) if (grid.active(rr, c)) cells.push({ r: rr, c });
        }
      });
      return { clearCells: uniq(cells), scoreBonus: 500, label: 'combo_prism_beams' };
    }
    case 'burst+burst': {
      const cells: Coord[] = [];
      for (let r = at.r - 2; r <= at.r + 2; r++) {
        for (let c = at.c - 2; c <= at.c + 2; c++) {
          if (grid.active(r, c)) cells.push({ r, c });
        }
      }
      return { clearCells: cells, scoreBonus: 320, label: 'combo_mega_burst' };
    }
    case 'burst+nova':
      return {
        ...activateNova(grid, at, 3),
        scoreBonus: 400,
        label: 'combo_mega_nova',
      };
    case 'nova+nova': {
      const cells: Coord[] = [];
      grid.forEachActive((r, c) => cells.push({ r, c }));
      // leave a few random-ish edges? full clear is spectacular
      return { clearCells: cells, scoreBonus: 600, label: 'combo_board_pulse' };
    }
    case 'nova+prism': {
      const kind = a.special === 'prism' ? a.prismKind ?? a.kind : b.prismKind ?? b.kind;
      const convert: ActivationResult['convertToSpecial'] = [];
      grid.forEachActive((r, c, cell) => {
        if (cell.piece?.kind === kind && !cell.piece.special) {
          convert.push({ at: { r, c }, special: 'burst', kind });
        }
      });
      return {
        clearCells: [{ r: at.r, c: at.c }],
        convertToSpecial: convert,
        scoreBonus: 450,
        label: 'combo_convert_bursts',
      };
    }
    case 'burst+prism': {
      const kind = a.special === 'prism' ? a.prismKind ?? a.kind : b.prismKind ?? b.kind;
      const cells = [
        ...activatePrism(grid, at, kind).clearCells,
        ...activateBurst(grid, at).clearCells,
      ];
      return { clearCells: uniq(cells), scoreBonus: 420, label: 'combo_prism_burst' };
    }
    case 'prism+prism': {
      const counts = new Map<PieceKind, number>();
      grid.forEachActive((_r, _c, cell) => {
        if (cell.piece) counts.set(cell.piece.kind, (counts.get(cell.piece.kind) ?? 0) + 1);
      });
      const sorted = [...counts.entries()].sort((x, y) => y[1] - x[1]);
      const kinds = sorted.slice(0, 2).map((x) => x[0]);
      const cells: Coord[] = [{ r: at.r, c: at.c }];
      grid.forEachActive((r, c, cell) => {
        if (cell.piece && kinds.includes(cell.piece.kind)) cells.push({ r, c });
      });
      return { clearCells: uniq(cells), scoreBonus: 700, label: 'combo_double_prism' };
    }
    default: {
      // fallback: union of both effects
      const ra = activateSpecial(grid, at, { ...a, special: a.special });
      const rb = activateSpecial(grid, at, { ...b, special: b.special });
      return {
        clearCells: uniq([...ra.clearCells, ...rb.clearCells]),
        scoreBonus: ra.scoreBonus + rb.scoreBonus + 100,
        label: `combo_${key}`,
      };
    }
  }
}

function uniq(cells: Coord[]): Coord[] {
  const m = new Map<string, Coord>();
  for (const c of cells) m.set(`${c.r},${c.c}`, c);
  return [...m.values()];
}

export function createSpecialPiece(
  kind: PieceKind,
  special: SpecialType,
  beamAxis?: 'h' | 'v'
): Omit<PieceId, 'id'> {
  return {
    kind,
    special,
    beamAxis: special === 'beam' ? beamAxis ?? 'h' : undefined,
    prismKind: special === 'prism' ? kind : undefined,
  };
}
